import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Info, ExternalLink } from "lucide-react";

interface WelcomeModalProps {
  isAuthenticated: boolean;
  onClose: () => void;
}

const LOCALSTORAGE_KEY = "hasSeenMarketplaceWelcome";
const LOCALSTORAGE_VERSION = "v2"; // Increment this if you want to show the modal again

export const WelcomeModal = ({ isAuthenticated, onClose }: WelcomeModalProps) => {
  const [open, setOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Check if user has seen the welcome modal
    const hasSeenWelcome = localStorage.getItem(LOCALSTORAGE_KEY);

    // Show modal if:
    // 1. User hasn't seen it before, OR
    // 2. The version has changed (to show updated content)
    if (!hasSeenWelcome || hasSeenWelcome !== LOCALSTORAGE_VERSION) {
      // Show modal after a short delay for better UX
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    // Mark as seen in localStorage
    localStorage.setItem(LOCALSTORAGE_KEY, LOCALSTORAGE_VERSION);
    setOpen(false);
    onClose();
  };

  const handleNext = () => {
    const maxSlides = 2; // 3 screens (0, 1, 2)
    if (currentSlide < maxSlides) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleSignUp = () => {
    handleClose();
    navigate("/register");
  };

  const handleLogin = () => {
    handleClose();
    navigate("/login");
  };

  // Render slide content
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        // Screen 1: Welcome to the ClaryNext Marketplace
        return (
          <>
            <DialogHeader className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-xl sm:text-3xl">✨</span>
                </div>
                <DialogTitle className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-indigo-900 bg-clip-text text-transparent leading-tight">
                  Welcome to ClaryNext Marketplace
                </DialogTitle>
              </div>
              <DialogDescription className="text-base space-y-4 sm:space-y-5">

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <p className="leading-relaxed text-gray-600 text-sm sm:text-base lg:text-lg">
                    Upgrade your emotional, thinking, and behavioral patterns with interactive gems of <span className="font-semibold text-gray-900">actionable wisdom</span>. Let the activity playbooks guide you to better outcomes and clarity.
                  </p>
                </div>
                {/* Welcome Image */}
                <div className="w-full rounded-xl overflow-hidden flex justify-center">
                  <img
                      src="/assets/images/welcome-modal.jpg"
                      alt="ClaryNext Marketplace Welcome"
                      className="w-full max-w-[300px] h-auto object-cover"
                  />
                </div>

              </div>
                <div className="bg-gradient-to-br from-primary/5 via-indigo-50/50 to-purple-50/30 rounded-xl p-4 sm:p-5 border border-primary/10/50">
                  <p className="leading-relaxed text-gray-700 text-sm sm:text-base">
                    Browse or search for actionable wisdom{" "}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="font-bold text-indigo-700 underline decoration-dotted cursor-pointer inline-flex items-center gap-1 hover:text-indigo-800 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-1 rounded-sm">
                          you really need
                          <Info className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="max-w-[280px] sm:max-w-sm bg-gradient-to-br from-indigo-900 to-purple-900 text-white border-0 shadow-xl p-3 sm:p-4" side="top" align="center">
                        <p className="text-xs sm:text-sm leading-relaxed">
                          💡 A headache pill won't do much for someone without a headache. A ClaryNext activity works best when you are working on something that matters and when you allow your brain to be immersed in the activity.
                        </p>
                      </PopoverContent>
                    </Popover>
                    , </p><p className="leading-relaxed text-gray-700 text-sm sm:text-base"><b>try</b> it, <b>buy</b> it, and<b> apply it</b> to your life.</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-3 flex items-center gap-2">
                    <span className="text-indigo-600">•</span> Mentoring credits from the author are often included or may be purchased separately.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
          </>
        );

      case 1:
        // Screen 2: Didn't find actionable wisdom that suits you?
        return (
          <>
            <DialogHeader className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-xl sm:text-3xl">🔍</span>
                </div>
                <DialogTitle className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 bg-clip-text text-transparent leading-tight">
                  Didn't find what you need?
                </DialogTitle>
              </div>
              <DialogDescription className="text-base">
                <p className="text-gray-600 mb-4 sm:mb-5 text-sm sm:text-base lg:text-lg">We've got you covered with these options:</p>
                <div className="space-y-2 sm:space-y-3">
                  <button
                    onClick={() => {
                      window.open('/marketplace?tab=services', '_blank');
                    }}
                    className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-primary/5/30 border border-gray-200 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-left w-full"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">💼</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Check <span className="font-semibold text-gray-900">"Services"</span> to have an actionable wisdom activity or program crafted for you.
                    </p>
                  </button>
                  <button
                    onClick={() => {
                      window.open('/contact?category=activity_request', '_blank');
                    }}
                    className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-indigo-50/30 border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer text-left w-full"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">💡</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      <span className="font-semibold text-gray-900">Suggest an activity.</span> We'll notify you when it becomes available.
                    </p>
                  </button>
                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-purple-50/30 border border-gray-200 hover:border-purple-300 hover:shadow-sm transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">🤖</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Create an activity using our <span className="font-semibold text-gray-900">experimental quick AI feature</span> <span className="text-[10px] sm:text-xs bg-purple-100 text-purple-700 px-1.5 sm:px-2 py-0.5 rounded-full ml-1">registration required</span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      window.open('/marketplace?tab=platform', '_blank');
                    }}
                    className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-violet-50/30 border border-gray-200 hover:border-violet-300 hover:shadow-sm transition-all cursor-pointer text-left w-full"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">✍️</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Get our <span className="font-semibold text-gray-900">authoring license</span> to fine-tune a quick activity or create one from scratch. A great way to foster awareness, communication, and growth.
                    </p>
                  </button>
                </div>
              </DialogDescription>
            </DialogHeader>
          </>
        );

      case 2:
        // Screen 3: For professionals
        return (
          <>
            <DialogHeader className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shadow-sm flex-shrink-0">
                  <span className="text-xl sm:text-3xl">🚀</span>
                </div>
                <DialogTitle className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-violet-900 bg-clip-text text-transparent leading-tight">
                  For professionals
                </DialogTitle>
              </div>
              <DialogDescription className="text-base">
                <p className="text-gray-600 mb-4 sm:mb-5 text-sm sm:text-base lg:text-lg">Ready to make an impact?</p>
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-primary/5/30 border border-gray-200 hover:border-primary/30 hover:shadow-sm transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br bg-primary flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">🎓</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      <span className="font-semibold text-gray-900">Make your wisdom actionable</span> and share it with your clients or the world. Become a mentor and/or vendor on our platform.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-indigo-50/30 border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">🏢</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      <span className="font-semibold text-gray-900">L&D organizations:</span> Improve effectiveness and reduce costs with our technology.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gradient-to-r from-white to-purple-50/30 border border-gray-200 hover:border-purple-300 hover:shadow-sm transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-base sm:text-xl">🌍</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                      Support our mission to build a <span className="font-semibold text-gray-900">wiser, more enlightened world</span>.
                    </p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-indigo-50 via-purple-50/50 to-violet-50/30 rounded-xl p-4 sm:p-6 border border-indigo-100/50 shadow-sm">
                  <p className="text-gray-700 text-center mb-3 sm:mb-4 font-medium text-sm sm:text-base">
                    Let's discuss how we can work together
                  </p>
                  <Button
                    onClick={() => {
                      window.open('https://calendly.com/clarynext/doron', '_blank');
                    }}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-10 sm:h-11 shadow-md text-sm sm:text-base"
                  >
                    <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    Set a meeting with Doron
                  </Button>
                </div>
              </DialogDescription>
            </DialogHeader>
          </>
        );

      default:
        return null;
    }
  };

  const maxSlides = 2; // 3 screens (0, 1, 2)
  const isLastSlide = currentSlide === maxSlides;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden border-0 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Subtle background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/8/40 via-indigo-100/40 to-purple-100/30 blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-100/30 to-primary/8/30 blur-3xl -z-10" />

        {/* Content wrapper */}
        <div className="relative bg-white rounded-xl">
          {/* Content section */}
          <div className="px-4 py-6 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            {renderSlide()}
          </div>

          {/* Footer */}
          <div className="px-4 py-4 sm:px-6 sm:py-5 lg:px-8 bg-gradient-to-br from-gray-50 to-primary/5/30 border-t border-gray-100">
            <DialogFooter className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <div className="flex gap-2 items-center order-2 sm:order-1">
                {/* Progress dots */}
                <div className="flex gap-1.5 sm:gap-2">
                  {Array.from({ length: maxSlides + 1 }).map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? "bg-gradient-to-r from-primary to-indigo-600 w-6 sm:w-8"
                          : "bg-gray-300 w-1.5 sm:w-2"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-[10px] sm:text-xs text-gray-500 ml-1 sm:ml-2">
                  {currentSlide + 1} / {maxSlides + 1}
                </span>
              </div>

              <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-gray-600 hover:text-gray-900 flex-1 sm:flex-none h-9 sm:h-10 text-sm"
                >
                  Skip
                </Button>

                {currentSlide > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="border-gray-300 hover:border-gray-400 flex-1 sm:flex-none h-9 sm:h-10 text-sm"
                  >
                    Back
                  </Button>
                )}

                <Button
                  onClick={handleNext}
                  className="min-w-24 sm:min-w-28 bg-gradient-to-r from-primary to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 shadow-md flex-1 sm:flex-none h-9 sm:h-10 text-sm sm:text-base"
                >
                  {isLastSlide ? "Get Started" : "Next"}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const checkShouldShowWelcomeModal = (): boolean => {
  const hasSeenWelcome = localStorage.getItem(LOCALSTORAGE_KEY);
  return !hasSeenWelcome || hasSeenWelcome !== LOCALSTORAGE_VERSION;
};
